import { z } from 'zod';

// ─── Enums ──────────────────────────────────────────
export const FEEDBACK_TYPES = ['BUG', 'IMPROVEMENT', 'QUESTION'] as const;
export const FEEDBACK_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export const FEEDBACK_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as const;
export const FEEDBACK_SOURCES = ['USER', 'QA', 'AUTO_CLIENT', 'AUTO_SERVER'] as const;

export type FeedbackType = (typeof FEEDBACK_TYPES)[number];
export type FeedbackPriority = (typeof FEEDBACK_PRIORITIES)[number];
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];
export type FeedbackSource = (typeof FEEDBACK_SOURCES)[number];

// ─── Form Schema ────────────────────────────────────
export const feedbackFormSchema = z.object({
    module: z.string().min(1, { message: 'Module không được để trống' }),
    pageUrl: z.string().min(1, { message: 'URL trang không được để trống' }),
    type: z.enum(FEEDBACK_TYPES, { required_error: 'Vui lòng chọn loại phản hồi' }),
    priority: z.enum(FEEDBACK_PRIORITIES, { required_error: 'Vui lòng chọn mức ưu tiên' }),
    title: z.string().min(1, { message: 'Tiêu đề không được để trống' }).max(200, { message: 'Tiêu đề tối đa 200 ký tự' }),
    description: z.string().optional(),
    images: z.array(z.string()).optional(),
    screenshotBefore: z.string().optional(),
});

export type FeedbackFormData = z.infer<typeof feedbackFormSchema>;

// ─── Full Interface ─────────────────────────────────
export interface Feedback {
    id: string;
    code: string;
    module: string;
    pageUrl: string;
    type: FeedbackType;
    priority: FeedbackPriority;
    title: string;
    description: string;
    images?: string[] | null;
    screenshotBefore?: string | null;
    screenshotAfter?: string | null;
    status: FeedbackStatus;
    reportedBy: string;
    assignedTo?: string | null;
    devResponse?: string | null;
    createdAt: string;
    updatedAt: string;
    comments?: any[];
    commentCount?: number;
    reportedByName?: string;
    assignedToName?: string;
    source?: FeedbackSource;
    diagnostics?: any;
    fingerprint?: string | null;
    occurrenceCount?: number;
    affectedUsers?: number;
    firstSeenAt?: string;
    lastSeenAt?: string;
    appVersion?: string | null;
    userAgent?: string | null;
}

// ─── Display Labels ─────────────────────────────────
export const FEEDBACK_SOURCE_LABELS: Record<FeedbackSource, string> = {
    USER: 'Người dùng',
    QA: 'QA Tester',
    AUTO_CLIENT: 'Hệ thống (Trình duyệt)',
    AUTO_SERVER: 'Hệ thống (Máy chủ)',
};
export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
    BUG: 'Lỗi',
    IMPROVEMENT: 'Cải tiến',
    QUESTION: 'Câu hỏi',
};

export const FEEDBACK_PRIORITY_LABELS: Record<FeedbackPriority, string> = {
    LOW: 'Thấp',
    MEDIUM: 'Trung bình',
    HIGH: 'Cao',
    CRITICAL: 'Khẩn cấp',
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
    OPEN: 'Mới',
    IN_PROGRESS: 'Đang xử lý',
    RESOLVED: 'Hoàn thành',
    CLOSED: 'Đã đóng',
};
